import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track } from 'modules/core/lib/Tracking'
import type { TentacleErrorData } from 'modules/core/lib/parseErrorData'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import Chart from 'modules/reports/components/Chart'
import { ShareReportsPolicy } from 'modules/reports/components/DashboardShow/types'
import Generator from 'modules/reports/components/Generator'
import ShareReportModal from 'modules/reports/components/ShareReportModal'
import ReportStruct from 'modules/reports/lib/ReportStruct'
import { events } from 'modules/reports/lib/events'
import Report, { type Variable } from 'modules/reports/store/models/Report'
import type { ResourceData } from 'modules/reports/store/models/Resource'

import Header from './Header'
import MoveReportModal from './MoveReportModal'
import * as styles from './index.vanilla.css'

const CHARTS_WITH_LEGEND = ['pie', 'bars', 'area', 'lines']

interface ContentProps {
  loading: boolean
  data?: ResourceData
  reportStruct: ReportStruct
  error?: TentacleErrorData | Error
}

const Content = ({ loading, data, reportStruct, error }: ContentProps) => {
  const applyLegend = CHARTS_WITH_LEGEND.includes(reportStruct.type)

  return (
    <Chart
      data={data}
      error={error}
      reportStruct={reportStruct}
      loading={loading}
      showLegend={applyLegend}
      reduceMargins
      withinDashboardItem
    />
  )
}

interface ItemProps {
  report: Report
  variables: Variable[]
  canEdit: boolean
  lastRefreshTimeString?: string
  reportsSharePolicy: ShareReportsPolicy[]
  contractVersions: ContractVersions
}

const Item = ({
  report,
  variables,
  canEdit,
  lastRefreshTimeString,
  reportsSharePolicy,
  contractVersions,
}: ItemProps) => {
  const reportStruct = React.useMemo(() => ReportStruct.buildFromReport(report), [report])
  const policyKey = 'reports.manage_report'
  const { loading: loadingPolicies } = useAsyncData(
    () => policyAuthorizations.authorizePolicies(policyKey, [report]),
    [report.id]
  )
  const canEditReport = policyAuthorizations.for(policyKey, report)
  const [isMoveOpened, setMoveOpened] = React.useState(false)
  const [isShareOpened, setShareOpened] = React.useState(false)

  const onMove = (isMoveOpenedValue = true) => {
    setMoveOpened(isMoveOpenedValue)
  }

  const onShare = (opened = true) => {
    track(events.clickReportShareFromWidget)
    setShareOpened(opened)
  }

  if (loadingPolicies) return <Loading />

  return (
    <ErrorBoundary>
      <Generator
        reportStruct={reportStruct}
        variables={variables}
        lastRefreshTimeString={lastRefreshTimeString}
      >
        {({ data, loading, error }) => {
          return (
            <>
              <Box width="full" height="full">
                <Header
                  reportStruct={reportStruct}
                  data={data}
                  canEditDashboard={canEdit}
                  canEditReport={canEditReport}
                  onMove={onMove}
                  onShare={() => onShare(true)}
                  variables={variables}
                  lastUpdated={data?.lastUpdated}
                  error={error}
                  reportsSharePolicy={reportsSharePolicy}
                />
                <Box width="full" height="full">
                  <div className={styles.content}>
                    <div
                      className={
                        reportStruct.type === 'table'
                          ? styles.contentInnerChartTable
                          : styles.contentInnerChartOther
                      }
                    >
                      <ErrorBoundary>
                        <Content
                          loading={loading}
                          data={data}
                          error={error}
                          reportStruct={reportStruct}
                        />
                      </ErrorBoundary>
                    </div>
                  </div>
                </Box>
              </Box>
              {isMoveOpened && report && (
                <MoveReportModal report={report} onClose={() => onMove(false)} />
              )}
              {isShareOpened && report && (
                <ShareReportModal
                  report={report}
                  onClose={() => onShare(false)}
                  contractVersions={contractVersions}
                />
              )}
            </>
          )
        }}
      </Generator>
    </ErrorBoundary>
  )
}

export default observer(Item)
